import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, Briefcase, FileCheck } from 'lucide-react';

/**
 * Directory card for a Pro listing. Renders ONLY the PII-safe card fields the API returns
 * (§11 — never phone/email/profile_id). The whole card links to the public detail page.
 */
const rateLabel = (listing, t) => {
  const { rate_type, rate_min, rate_max } = listing;
  if (rate_type === 'negotiable' || (rate_min == null && rate_max == null)) {
    return t('services.negotiable');
  }
  const amount = rate_min != null ? rate_min : rate_max;
  const suffix =
    rate_type === 'hourly' ? t('services.perHour')
    : rate_type === 'per_project' ? ` ${t('services.perProject')}`
    : ` ${t('services.perService')}`;
  return `${t('services.from')} ₱${Number(amount).toLocaleString()}${suffix}`;
};

const ProListingCard = ({ listing }) => {
  const { t } = useTranslation();
  const areas = listing.service_area || [];
  const photoUrl = listing.photo && listing.photo.url ? listing.photo.url : null;
  const extraAreas = areas.length - 2;

  return (
    <Link
      to={`/services/listings/${listing.slug}`}
      className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <Card className="overflow-hidden h-full">
        {photoUrl ? (
          <img src={photoUrl} alt={listing.title} className="w-full h-40 object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-40 bg-muted flex items-center justify-center" aria-hidden="true">
            <Briefcase className="text-muted-foreground" size={28} />
          </div>
        )}
        <div className="p-4">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold break-words">{listing.title}</h3>
            <div className="flex flex-col items-end gap-1 shrink-0">
              {listing.id_verified && (
                <Badge variant="verified" className="px-2 py-0.5 text-[10px]">
                  {t('services.verified')}
                </Badge>
              )}
            </div>
          </div>
          <p className="text-base font-semibold text-primary mt-3">{rateLabel(listing, t)}</p>
          {listing.category && listing.category.name && (
            <p className="text-sm text-muted-foreground mt-1">{listing.category.name}</p>
          )}
          {areas.length > 0 && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2 min-w-0">
              <MapPin size={14} className="shrink-0" aria-hidden="true" />
              <span className="truncate">
                {areas.slice(0, 2).join(', ')}
                {extraAreas > 0 ? ` ${t('services.areasMore', { count: extraAreas })}` : ''}
              </span>
            </div>
          )}
          {Array.isArray(listing.credential_types) && listing.credential_types.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2 min-w-0">
              <FileCheck size={13} className="shrink-0 text-emerald-600" aria-hidden="true" />
              <span className="truncate">
                {listing.credential_types.map((c) => t(`services.verifDocType_${c}`, c)).join(', ')}
              </span>
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
};

export default ProListingCard;
